/**
 * The voices panel (R22; D192 per speaker): everything each declared speaker
 * has said, each line under the act it commented on.
 *
 * @remarks
 * Pure and prop-driven. It renders the engine's {@link VoiceIndexEntry}
 * derivation and computes nothing: `deriveVoiceIndex` is the single source of
 * truth for what was said and what the player was doing at the time. Text is
 * FROZEN by that derivation — this surface recalls the record, never re-reads
 * the world.
 *
 * One index per speaker, in the pack's declared order. With ONE speaker the
 * panel is the flat list it always was; with more, each speaker's index sits
 * under a heading of its declared name — one door, one dialog, the shape the
 * control row can afford (D172); a dialog per voice is the alternative, Mike's to rule.
 *
 * Accessibility: an ordered list per speaker — session order is the story of
 * the commentary — with one `<article>` per utterance named by its act
 * label. The line carries the speaker styling class and the speaker's color
 * so it reads in the same register here as it did in the flow (the quotes
 * are authored — the channel's one signature that survives every medium).
 * The act label sits above it in the small caption register, with the
 * player's own words beside it when the turn had an echo.
 *
 * @packageDocumentation
 */

import type { CSSProperties } from 'react';

import { type SpeakerView, type VoiceIndexView } from '@orpheus/api-contracts';

import { RevealedTextComponent } from '../revealed-text/revealed-text-component.js';

/** Props for {@link VoicesPanelComponent}. */
export interface VoicesPanelComponentProps {
	/** The pack's declared speakers — the color each index's lines wear. */
	readonly speakers: readonly SpeakerView[];
	/** The engine's voice-index derivation, one per declared speaker — utterances, oldest first. */
	readonly voices: readonly VoiceIndexView[];
}

/** How many lines every speaker has spoken, together — the heading's count. */
export const spokenLineCountOf = (voices: readonly VoiceIndexView[]): number => voices.reduce((total, voice) => total + voice.entries.length, 0);

/**
 * Render the voices panel: heading, then each speaker's utterances in
 * session order.
 *
 * @param props - {@link VoicesPanelComponentProps}
 * @returns The rendered panel
 */
export const VoicesPanelComponent = ({ speakers, voices }: VoicesPanelComponentProps) => {
	const spokenCount = spokenLineCountOf(voices);

	return (
		<section aria-labelledby="voices-panel-heading" className="voices-panel">
			<h2 className="voices-panel__heading" id="voices-panel-heading">
				Voices — {spokenCount} line{spokenCount === 1 ? '' : 's'} spoken
			</h2>
			{spokenCount === 0 ? (
				<p className="voices-panel__empty">Nothing spoken yet. What the voice says is kept here.</p>
			) : (
				voices.map((voice) => (
					<VoiceIndexSection isHeaded={voices.length > 1} key={voice.speakerId} speaker={speakers.find((candidate) => candidate.speakerId === voice.speakerId)} voice={voice} />
				))
			)}
		</section>
	);
};

/** One speaker's index: a heading when there is more than one voice to tell apart, then the list. */
const VoiceIndexSection = ({ isHeaded, speaker, voice }: { readonly isHeaded: boolean; readonly speaker: SpeakerView | undefined; readonly voice: VoiceIndexView }) => {
	if (voice.entries.length === 0) {
		return null;
	}

	return (
		<section aria-label={voice.name} className="voices-panel__speaker">
			{!!isHeaded && <h3 className="voices-panel__speaker-name">{voice.name}</h3>}
			<ol className="voices-panel__utterances">
				{voice.entries.map((entry, entryIndex) => (
					<li
						className="voices-panel__utterance"
						// Position IS identity: the index is the transcript's order, and
						// an identical line said twice is two utterances on purpose.
						// eslint-disable-next-line react/no-array-index-key -- ordered projection of the transcript, never reordered
						key={entryIndex}
					>
						<article aria-label={entry.actLabel}>
							<p className="voices-panel__act">
								<span className="voices-panel__act-label">{entry.actLabel}</span>
								{entry.duringInput !== undefined && (
									<span className="voices-panel__act-echo">
										{/* D85's sigil grammar: ▷ immediately precedes the player's
										    words, real text, hidden from assistive names — the words
										    are the content, the sigil is the channel signature. */}
										<span aria-hidden="true">{'▷ '}</span>
										{entry.duringInput}
									</span>
								)}
							</p>
							<p
								className="voices-panel__line voices-panel__line--speaker"
								style={speaker?.color === undefined ? undefined : ({ '--color-voice-line': speaker.color } as CSSProperties)}
							>
								{/* D145: AS HEARD — the frozen text through the same renderer
								    as the flow, so a redaction is a bar here too, and a line
								    cleared since it was spoken still reads as it was spoken. */}
								<RevealedTextComponent cadence="instant" text={entry.text} />
							</p>
						</article>
					</li>
				))}
			</ol>
		</section>
	);
};
